import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Eye, EyeOff, Grape } from 'lucide-react';
import PublicHeader from '../components/PublicHeader';
import toast from 'react-hot-toast';
import { buildVerticalPath, getVerticalBranding, type VerticalType } from '../config/vertical';

interface LoginForm {
  username: string;
  password: string;
}

interface LoginProps {
  vertical?: VerticalType;
}

const Login: React.FC<LoginProps> = ({ vertical = 'vineyard' }) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();
  const { brandName } = getVerticalBranding(vertical);
  const isGreenhouse = vertical === 'greenhouse';
  const isOrchard = vertical === 'orchard';
  const unitLabel = isGreenhouse ? 'greenhouse zones' : 'operations';
  const focusClass = isGreenhouse
    ? 'focus:ring-leaf-400 focus:border-leaf-400'
    : isOrchard
      ? 'focus:ring-blue-400 focus:border-blue-400'
      : 'focus:ring-grape-400 focus:border-grape-400';
  const primaryButtonClass = isGreenhouse
    ? 'bg-gradient-to-br from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 focus:ring-green-500'
    : isOrchard
      ? 'bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 focus:ring-blue-500'
      : 'bg-gradient-to-br from-grape-400 to-grape-600 hover:from-grape-500 hover:to-grape-700 focus:ring-grape-400';
  const linkClass = isGreenhouse
    ? 'text-green-700 hover:text-green-800'
    : isOrchard
      ? 'text-blue-700 hover:text-blue-800'
      : 'text-grape-600 hover:text-grape-700';
  const formCardBorderClass = isGreenhouse ? 'border-green-200' : isOrchard ? 'border-blue-200' : 'border-grape-700';
  const inputBorderClass = isGreenhouse ? 'border-green-300' : isOrchard ? 'border-blue-300' : 'border-wood-300';

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      const userData = await login(data.username, data.password);
      // Get the user's display name (full_name if available, otherwise username)
      const displayName = userData?.full_name || userData?.username || 'User';
      toast.success(`Welcome to ${brandName}, ${displayName}!`);
      
      // Navigate based on user role - workers go to worker dashboard, others to main dashboard
      // Note: We use userData directly here since login() sets state synchronously before returning
      if (userData?.role === 'worker') {
        navigate('/worker-dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (error: any) {
      // Provide more specific error message if available
      const errorMessage = error?.response?.data?.detail || 'Invalid username or password';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-vineyard-light via-leaf-50/50 to-grape-50/40 relative overflow-hidden">
      {/* Subtle ambient shapes for depth */}
      <div className="absolute top-1/4 -left-24 w-72 h-72 rounded-full bg-grape-200/25 blur-3xl pointer-events-none" aria-hidden />
      <div className="absolute bottom-1/4 -right-24 w-96 h-96 rounded-full bg-leaf-200/20 blur-3xl pointer-events-none" aria-hidden />

      <PublicHeader vertical={vertical} />

      <div className="relative flex items-center justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md animate-slide-up">
        <div className={`bg-vineyard-white py-6 sm:py-8 px-4 sm:px-6 lg:px-10 rounded shadow border-2 ${formCardBorderClass}`}>
          <div>
            <div className="flex justify-center mb-4">
              <div className={`flex items-center justify-center w-14 h-14 rounded-full ring-2 ${isGreenhouse ? 'bg-green-100 ring-green-200/60' : isOrchard ? 'bg-blue-100 ring-blue-200/60' : 'bg-grape-100 ring-grape-200/60'}`} aria-hidden>
                <Grape className={`h-7 w-7 ${isGreenhouse ? 'text-green-600' : isOrchard ? 'text-blue-600' : 'text-grape-600'}`} />
              </div>
            </div>
            <h2 className="text-center text-2xl sm:text-3xl font-extrabold text-wood-900 font-brand">
              Sign in to {brandName}
            </h2>
            <p className="mt-2 text-center text-sm sm:text-base text-metal-600">
              Manage your {unitLabel} with precision
            </p>
          </div>
          <form className="mt-6 sm:mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-wood-700 mb-1">
                  Username or Email
                </label>
                <input
                  {...register('username', { required: 'Username or email is required' })}
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  className={`w-full px-3 py-3 sm:py-2 border-2 ${inputBorderClass} rounded-lg text-base sm:text-sm text-wood-900 placeholder-metal-400 focus:outline-none focus:ring-2 ${focusClass} transition-all duration-200`}
                  placeholder="Username or Email"
                />
                {errors.username && (
                  <p className="mt-1 text-sm text-grape-600">{errors.username.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-wood-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    {...register('password', { required: 'Password is required' })}
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                  className={`w-full px-3 py-3 sm:py-2 pr-10 border-2 ${inputBorderClass} rounded-lg text-base sm:text-sm text-wood-900 placeholder-metal-400 focus:outline-none focus:ring-2 ${focusClass} transition-all duration-200`}
                    placeholder="Password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center touch-manipulation"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-metal-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-metal-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-grape-600">{errors.password.message}</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full flex justify-center py-3 sm:py-2 px-4 border border-transparent text-base sm:text-sm font-medium rounded-lg text-vineyard-white ${primaryButtonClass} focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.99] transition-all duration-300`}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm sm:text-base text-metal-600">
                Don't have an account?{' '}
                <Link
                  to={buildVerticalPath(vertical, '/signup')}
                  className={`font-medium ${linkClass} transition-colors duration-200`}
                >
                  Sign up
                </Link>
              </p>
              <p className="mt-2 text-sm text-metal-600">
                <button
                  type="button"
                  onClick={() => navigate(buildVerticalPath(vertical, '/reset-password'))}
                  className={`font-medium ${linkClass} transition-colors duration-200`}
                >
                  Forgot your password?
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Login; 
